import { useEffect, useState } from "react";

import { ArrowRight, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Card, CardContent } from "src/components/ui/card";
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "src/components/ui/responsive-dialog";
import { useAuth } from "src/lib/auth";
import { useApiQuery } from "src/lib/hooks/useApi";
import {
  getVolumeBand,
  ordersToVolumeSliderPosition,
  VOLUME_SLIDER_LABELS,
  VOLUME_SLIDER_STOPS,
  volumeSliderPositionToOrders,
} from "src/lib/pricing";
import {
  PAYMENT_METHOD_QUERY,
  PAYMENT_METHOD_URL_QUERY,
} from "src/lib/queries/billing";

const APP_NAME = process.env.APP_NAME || "the app";

const currency = (cents) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);

const paywallCopy = {
  onboarding: {
    title: "Add a payment method",
    description:
      "Add a card once to keep billing ready and avoid interruptions as you finish setup.",
  },
  store: {
    title: "Add a payment method to connect another store",
    description:
      "Your trial has ended. Add a card once and keep future store connections uninterrupted.",
  },
  trialEnded: {
    title: "Your free trial has ended",
    description: `Please add a payment method to keep using ${APP_NAME} without interruption`,
  },
  billingPaused: {
    title: "Add a payment method to resume store syncing",
    description:
      "Store syncing is paused because billing needs attention. Add a card once to resume updates and keep your analytics current.",
  },
};

export function PaywallDialog({
  onClick,
  children,
  source = "store",
  open: controlledOpen,
  onOpenChange,
}) {
  const { currentUser } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const [isCheckingBilling, setIsCheckingBilling] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [estimatedOrders, setEstimatedOrders] = useState(500);
  const { queryApi, loading } = useApiQuery({
    query: PAYMENT_METHOD_QUERY,
  });
  const { queryApi: queryPaymentMethodUrl, loading: loadingPaymentMethodUrl } =
    useApiQuery({
      query: PAYMENT_METHOD_URL_QUERY,
    });
  const copy = paywallCopy[source] || paywallCopy.store;
  const estimatedBand = getVolumeBand(estimatedOrders);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = (nextOpen) => {
    if (typeof onOpenChange === "function") {
      onOpenChange(nextOpen);
    }

    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
  };

  const openPaymentMethodPage = async () => {
    setBillingError("");
    const { data, error } = await queryPaymentMethodUrl();

    if (error || !data?.paymentMethodUrl) {
      setBillingError(
        "We couldn't open the payment setup page right now. Please try again in a moment."
      );
      toast.error("Unable to open payment setup", {
        description: "Please try again in a moment.",
      });
      return;
    }

    if (data?.paymentMethodUrl) {
      window.open(data.paymentMethodUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Failed to process your request. Please try again.");
    }
  };

  const handleOpenChange = (state) => {
    if (!state) {
      setOpen(false);
      return;
    }

    setOpen(true);
  };

  useEffect(() => {
    let active = true;

    const checkBillingAccess = async () => {
      if (!open) {
        return;
      }

      if (currentUser?.trialDaysLeft !== 0) {
        onClick?.();
        if (active) {
          setOpen(false);
        }
        return;
      }

      setIsCheckingBilling(true);
      setBillingError("");

      const { data, error } = await queryApi();

      if (!active) {
        return;
      }

      const paymentMethod = data?.paymentMethod || null;

      setIsCheckingBilling(false);

      if (error) {
        setBillingError(
          "We couldn't verify your billing setup. You can still try opening the payment setup below."
        );
        return;
      }

      if (!paymentMethod?.last4) {
        return;
      }

      // Otherwise, close the dialog and call the onClick function
      onClick?.();
      setOpen(false);
    };

    checkBillingAccess();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      {children && (
        <ResponsiveDialogTrigger asChild disabled={loading}>
          {children}
        </ResponsiveDialogTrigger>
      )}

      <ResponsiveDialogContent
        className="md:max-w-4xl"
        mobileClassName="max-h-[92dvh] px-4 pb-[max(1rem,env(safe-area-inset-bottom))] data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=bottom]:rounded-t-2xl"
        desktopClassName="overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/12 via-background to-background" />
        <div className="absolute -top-24 right-[-2rem] h-72 w-72 rounded-full bg-primary/14 blur-3xl" />
        <div className="absolute -bottom-24 left-[-1rem] h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

        <div className="relative grid gap-3 md:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.7fr)] md:gap-4">
          <div className="flex flex-col justify-between py-2 md:py-4">
            <ResponsiveDialogHeader className="text-left">
              <Badge
                variant="outline"
                className="mb-3 hidden w-fit gap-2 rounded-full border-primary/30 bg-primary/8 px-3 py-1.5 text-[11px] font-medium tracking-[0.24em] text-muted-foreground uppercase md:mb-4 md:inline-flex"
              >
                <CreditCard className="size-4 text-primary" />
                Billing Required
              </Badge>

              <ResponsiveDialogTitle className="text-2xl/8 font-semibold tracking-tight sm:text-3xl/10">
                {copy.title}
              </ResponsiveDialogTitle>
              <ResponsiveDialogDescription className="mt-2 max-w-xl text-sm/6 text-muted-foreground sm:mt-3 sm:text-base/7">
                {copy.description}
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>

            <Card className="mt-6 gap-0 border-primary/20 bg-card/70 py-0 shadow-none md:mt-8">
              <CardContent className="px-4 py-4 sm:px-5 sm:py-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold sm:text-base">
                      Estimate your monthly price
                    </div>
                    <p className="mt-1 text-xs/5 text-muted-foreground sm:text-sm/6">
                      Move the slider to match this store&apos;s typical monthly
                      order volume.
                    </p>
                  </div>
                </div>

                <div className="mt-5">
                  <label
                    id="paywall-orders-label"
                    htmlFor="paywall-orders-slider"
                    className="sr-only"
                  >
                    Estimated orders per month
                  </label>
                  <input
                    id="paywall-orders-slider"
                    aria-labelledby="paywall-orders-label"
                    type="range"
                    min="0"
                    max={VOLUME_SLIDER_STOPS.length - 1}
                    step="0.01"
                    value={ordersToVolumeSliderPosition(estimatedOrders)}
                    className="accent-primary h-2 w-full cursor-pointer"
                    onChange={(event) =>
                      setEstimatedOrders(
                        volumeSliderPositionToOrders(Number(event.target.value))
                      )
                    }
                  />
                  <div className="relative mx-[7px] mt-1.5 h-4 text-[9px] text-muted-foreground sm:text-[10px]">
                    {VOLUME_SLIDER_LABELS.map((label, index) => (
                      <span
                        key={label}
                        className={`absolute top-0 whitespace-nowrap ${
                          index === 0
                            ? "translate-x-0"
                            : index === VOLUME_SLIDER_LABELS.length - 1
                              ? "-translate-x-full"
                              : "-translate-x-1/2"
                        }`}
                        style={{
                          left: `${(index / (VOLUME_SLIDER_LABELS.length - 1)) * 100}%`,
                        }}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {billingError && (
              <div className="mt-5 rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive-foreground">
                {billingError}
              </div>
            )}
          </div>

          <div className="flex flex-col justify-between border-t border-border/70 bg-muted/20 py-3 md:border-t-0 md:border-l md:px-4 md:py-4">
            <Card className="mb-3 rounded-[24px] border-border/70 bg-card/80 py-0 shadow-sm backdrop-blur md:mb-4">
              <CardContent className="px-4 py-3 sm:px-5 sm:py-5">
                <div className="text-muted-foreground text-[11px] font-medium tracking-[0.22em] uppercase">
                  Estimated monthly price
                </div>
                <div className="mt-3 text-sm font-semibold text-muted-foreground">
                  {estimatedOrders.toLocaleString("en-US")} orders/month
                </div>
                <div className="mt-4 flex items-end gap-x-2">
                  <span className="text-5xl font-semibold tracking-[-0.05em]">
                    {estimatedBand.customQuote
                      ? "Custom"
                      : currency(estimatedBand.priceCents)}
                  </span>
                  {!estimatedBand.customQuote && (
                    <span className="pb-1 text-sm text-muted-foreground">
                      /month
                    </span>
                  )}
                </div>

                {estimatedBand.customQuote && (
                  <p className="mt-3 text-xs/5 text-muted-foreground">
                    Provisionally billed at {currency(estimatedBand.priceCents)}
                    /month while we prepare a custom quote.
                  </p>
                )}
              </CardContent>
            </Card>

            <ResponsiveDialogFooter className="flex-col gap-2 sm:flex-col">
              <p className="mb-1 text-left text-xs/5 text-muted-foreground">
                This slider is an estimate. <br />
                Each store is priced separately using its average monthly orders
                over the last 365 days.
              </p>
              <Button
                size="lg"
                disabled={
                  isCheckingBilling || loading || loadingPaymentMethodUrl
                }
                onClick={openPaymentMethodPage}
              >
                {loadingPaymentMethodUrl ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <>
                    Add Payment Method
                    <ArrowRight className="size-5" />
                  </>
                )}
              </Button>
              <ResponsiveDialogClose asChild>
                <Button variant="outline" size="lg">
                  Cancel
                </Button>
              </ResponsiveDialogClose>
            </ResponsiveDialogFooter>
          </div>
        </div>

        {isCheckingBilling && (
          <div className="bg-background/80 absolute inset-0 flex items-center justify-center backdrop-blur-sm">
            <div className="bg-card text-card-foreground flex flex-col items-center gap-3 rounded-3xl border border-border px-6 py-5 text-center shadow-lg">
              <Loader2 className="text-primary size-7 animate-spin" />
              <div className="text-sm font-medium">
                Checking your billing setup
              </div>
              <div className="text-muted-foreground max-w-xs text-sm">
                We&apos;re confirming whether you already have a saved payment
                method.
              </div>
            </div>
          </div>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
